// =============================================================================
// SETUP CARD (Phase 4 - "all green, we handle the rest")
// =============================================================================
// Rendered at the top of the Dashboard. It is the whole onboarding: the merchant
// lands on the dashboard and this card guides them to connect at least one
// channel. Connecting Google auto-configures the PURCHASE action server-side, so
// one click is enough to go live. Once the pixel emits AND >=1 channel is live,
// the card flips to a single green "tracking is active" state.
// =============================================================================

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Banner,
  Badge,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Box,
  Divider,
  Icon,
} from '@shopify/polaris';
import { CheckCircleIcon, AlertCircleIcon } from '@shopify/polaris-icons';
import {
  useOnboardingStatus,
  useDeclareFunnelTags,
  useGoogleAdsAuthUrl,
  useConnectMeta,
  useConnectGA4,
  useConnectTikTok,
  useConnectPinterest,
  useConnectSnapchat,
  useTestGoogleAds,
  type ChannelStatus,
  type OnboardingStatus, type FunnelChannelKey, type FunnelDecision,
} from '../hooks/api';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';

// Redirect the top frame to an OAuth URL. Awaiting the mutation breaks the user
// gesture chain, so we must use a same-page redirect (not window.open) to avoid
// popup blockers - same idiom the per-channel pages use.
function redirectTop(url: string) {
  if (window.top) {
    window.top.location.href = url;
  } else {
    window.location.href = url;
  }
}

type ChannelKey = 'googleAds' | 'meta' | 'ga4' | 'tiktok' | 'pinterest' | 'snapchat';

interface ChannelRowProps {
  name: string;
  status: ChannelStatus;
  onConnect: () => void;
  onManage: () => void;
  connecting: boolean;
  t: ReturnType<typeof useTranslation>;
}

function ChannelRow({ name, status, onConnect, onManage, connecting, t }: ChannelRowProps) {
  // Google is only "done" when connected AND configured (account + purchase
  // action). Every other channel is done once connected + enabled + healthy.
  const done = status.configured !== undefined
    ? status.connected && status.configured && status.enabled && status.healthy
    : status.connected && status.enabled && status.healthy;
  const needsAction = status.connected && !done;
  // Surface WHY it needs action inline (audit P2-5): the merchant should not
  // have to open the channel page to discover "no account selected".
  const issueText = needsAction && status.issues && status.issues.length > 0 ? status.issues[0] : null;

  return (
    <BlockStack gap="100">
      <InlineStack align="space-between" blockAlign="center" gap="200">
        <InlineStack gap="200" blockAlign="center">
          {done ? (
            <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
          ) : needsAction ? (
            <Box><Icon source={AlertCircleIcon} tone="caution" /></Box>
          ) : null}
          <Text as="span" variant="bodyMd" fontWeight="semibold">{name}</Text>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          {done ? (
            <Badge tone="success">{t.setup.connected}</Badge>
          ) : needsAction ? (
            <Button size="slim" onClick={onManage}>{t.setup.needsAction}</Button>
          ) : (
            <Button size="slim" variant="primary" onClick={onConnect} loading={connecting}>
              {t.setup.connect}
            </Button>
          )}
        </InlineStack>
      </InlineStack>
      {issueText && (
        <Text as="p" variant="bodySm" tone="subdued">{issueText}</Text>
      )}
    </BlockStack>
  );
}

// Step 1 (Shopify): the theme app embed is the ONLY gclid capture path, and
// Shopify cannot auto-enable it. Until its heartbeat is seen, this row stays
// the merchant's top action; the deep link opens the theme editor with the
// embed pre-activated (one Save click).
//
// It used to say "+ 1-year server-cookie path", which is the WooCommerce story.
// Shopify's App Proxy strips Set-Cookie from proxied responses, so /init cannot
// set a browser cookie at all (verified live 2026-07-22). Persistence here is
// the embed's localStorage and first-party JS cookies, the server-side session
// row, and the identity graph - see CLAUDE.md.
//
// "Stale" = it beat once, then the storefront kept getting visits without it
// (turned off, or the theme was switched). Lumina showed "active" for a month
// in that state, because the old check was "ever seen".
function ClickTrackerStep({ status, t }: { status: OnboardingStatus; t: ReturnType<typeof useTranslation> }) {
  const { language } = useLanguage();
  if (status.platform !== 'SHOPIFY') return null;
  if (status.clickTrackerActive) {
    return (
      <InlineStack gap="200" blockAlign="center">
        <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
        <Text as="span" variant="bodyMd" fontWeight="semibold">{t.setup.clickTrackerActiveTitle}</Text>
        <Badge tone="success">{t.setup.clickTrackerActive}</Badge>
      </InlineStack>
    );
  }
  const stale = status.clickTrackerStale && !!status.clickTrackerLastSeenAt;
  const lastSeen = stale
    ? new Date(status.clickTrackerLastSeenAt!).toLocaleDateString(localeMap[language], { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  return (
    <Banner
      tone="warning"
      title={stale ? t.setup.clickTrackerStaleTitle : t.setup.clickTrackerTitle}
      action={status.themeEditorUrl ? { content: t.setup.clickTrackerCta, url: status.themeEditorUrl, external: true } : undefined}
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd">{stale ? t.setup.clickTrackerStaleBody.replace('{date}', lastSeen) : t.setup.clickTrackerBody}</Text>
        <Text as="p" variant="bodySm" tone="subdued">{t.setup.clickTrackerHint}</Text>
      </BlockStack>
    </Banner>
  );
}

export function SetupCard() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { data: status, isLoading } = useOnboardingStatus();
  const declareTags = useDeclareFunnelTags();
  const [connectError, setConnectError] = useState<string | null>(null);

  const googleAuth = useGoogleAdsAuthUrl();
  const testGoogle = useTestGoogleAds();
  const [testResultMsg, setTestResultMsg] = useState<string | null>(null);
  const connectMeta = useConnectMeta();
  const connectGA4 = useConnectGA4();
  const connectTikTok = useConnectTikTok();
  const connectPinterest = useConnectPinterest();
  const connectSnapchat = useConnectSnapchat();

  // Each channel: display name, the OAuth starter (returns {url} or {authUrl}),
  // and the settings route to finish configuration if a connect leaves work.
  const channels: Array<{
    key: ChannelKey;
    name: string;
    start: () => Promise<{ url?: string; authUrl?: string }>;
    pending: boolean;
    path: string;
  }> = [
    { key: 'googleAds', name: 'Google Ads', start: () => googleAuth.mutateAsync(), pending: googleAuth.isPending, path: '/google-ads' },
    { key: 'meta', name: 'Meta CAPI', start: () => connectMeta.mutateAsync(), pending: connectMeta.isPending, path: '/meta' },
    { key: 'ga4', name: 'Google Analytics 4', start: () => connectGA4.mutateAsync(), pending: connectGA4.isPending, path: '/ga4' },
    { key: 'tiktok', name: 'TikTok', start: () => connectTikTok.mutateAsync(), pending: connectTikTok.isPending, path: '/tiktok' },
    { key: 'pinterest', name: 'Pinterest', start: () => connectPinterest.mutateAsync(), pending: connectPinterest.isPending, path: '/pinterest' },
    { key: 'snapchat', name: 'Snapchat', start: () => connectSnapchat.mutateAsync(), pending: connectSnapchat.isPending, path: '/snapchat' },
  ];

  const handleConnect = useCallback(async (start: () => Promise<{ url?: string; authUrl?: string }>) => {
    setConnectError(null);
    try {
      const result = await start();
      const url = result.authUrl || result.url;
      if (url) redirectTop(url);
      else setConnectError(t.setup.connectFailed);
    } catch (err: any) {
      setConnectError(err?.message || t.setup.connectFailed);
    }
  }, [t]);

  const handleDeclare = useCallback((channel: FunnelChannelKey, keepsTheirTag: boolean) => {
    declareTags.mutate({ [channel]: keepsTheirTag });
  }, [declareTags]);

  // Send a 0.01 test conversion through the REAL Google pipeline so the setup
  // session can end green without waiting for organic traffic.
  const handleTestEvent = useCallback(async () => {
    setTestResultMsg(null);
    try {
      const result = await testGoogle.mutateAsync();
      setTestResultMsg(result.success ? t.setup.testEventOk : (result.error || t.setup.testEventFail));
    } catch (err: any) {
      setTestResultMsg(err?.message || t.setup.testEventFail);
    }
  }, [testGoogle, t]);

  // Don't flash the setup card before we know the state; the dashboard renders
  // fine without it.
  if (isLoading || !status) return null;

  // ---------------------------------------------------------------------------
  // Forwarding paused (over quota + unentitled). Highest-priority banner: data
  // is still captured, forwarding is paused, upgrading resumes it AND backfills
  // everything we held. Honest + high-converting.
  // ---------------------------------------------------------------------------
  const pausedBanner = status.forwardingPaused ? (
    <Banner
      tone="warning"
      title={t.setup.pausedTitle}
      action={{ content: t.setup.pausedCta, onAction: () => navigate('/billing') }}
    >
      <Text as="p" variant="bodyMd">
        {status.quotaPausedCount > 0
          ? t.setup.pausedBodyCount.replace('{count}', String(status.quotaPausedCount))
          : t.setup.pausedBody}
      </Text>
    </Banner>
  ) : null;

  // ---------------------------------------------------------------------------
  // ACTIVE: the single green state. "Nothing else to do, we handle the rest."
  // ---------------------------------------------------------------------------
  if (status.active) {
    const liveNames = status.liveChannels.map((k) => {
      const c = channels.find((ch) => ch.key === k);
      return c ? c.name : k;
    });
    return (
      <BlockStack gap="400">
        {pausedBanner}
        {/* Even when active (pixel + a channel), a missing click tracker means
            degraded attribution (no gclid capture, no 1-year cookies) - keep
            warning until its heartbeat is seen. */}
        {!status.clickTrackerActive && <ClickTrackerStep status={status} t={t} />}
        <Card>
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center">
            <Box><Icon source={CheckCircleIcon} tone="success" /></Box>
            <Text as="h2" variant="headingMd">{t.setup.activeTitle}</Text>
            <Badge tone="success">{t.setup.activeBadge}</Badge>
          </InlineStack>
          {/* "Nothing else to do" under a "turn the click tracker on" banner
              contradicts it: say there is one action left instead. */}
          <Text as="p" variant="bodyMd" tone="subdued">
            {status.platform === 'SHOPIFY' && !status.clickTrackerActive ? t.setup.activeBodyActionNeeded : t.setup.activeBody}
          </Text>
          {/* The embed's state stays in view once it is on, not only when it
              is missing: a merchant should see it, not infer it from silence. */}
          {status.clickTrackerActive && <ClickTrackerStep status={status} t={t} />}
          {liveNames.length > 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              {t.setup.activeLiveChannels}: {liveNames.join(', ')}
            </Text>
          )}
          {status.funnelRouting && (
            <FunnelRouting
              routing={status.funnelRouting}
              declared={status.funnelDeclaredTags}
              channels={status.channels}
              pending={declareTags.isPending}
              onDeclare={handleDeclare}
              t={t}
            />
          )}
        </BlockStack>
        </Card>
      </BlockStack>
    );
  }

  // ---------------------------------------------------------------------------
  // NOT ACTIVE: the setup checklist. One connected channel is enough to go live.
  // ---------------------------------------------------------------------------
  return (
    <BlockStack gap="400">
      {pausedBanner}
      <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">{t.setup.inactiveTitle}</Text>
          <Text as="p" variant="bodyMd" tone="subdued">{t.setup.inactiveBody}</Text>
        </BlockStack>

        {/* Step 1 (Shopify): activate the click tracker - the prerequisite for
            gclid capture + 1-year server cookies. */}
        <ClickTrackerStep status={status} t={t} />

        {/* Pixel signal - reassures the merchant capture is running before any
            channel is even connected. */}
        <InlineStack gap="200" blockAlign="center">
          {status.pixelEmitting ? (
            <><Box><Icon source={CheckCircleIcon} tone="success" /></Box>
              <Text as="span" variant="bodySm">{t.setup.pixelLive}</Text></>
          ) : (
            <BlockStack gap="100">
              <Text as="span" variant="bodySm" tone="subdued">{t.setup.pixelWaiting}</Text>
              {/* The green state should be reachable INSIDE the setup session:
                  once Google is configured, a test conversion proves the chain
                  without waiting for organic traffic (audit P1-3). */}
              {status.channels.googleAds.connected && (status.channels.googleAds as any).configured && (
                <InlineStack gap="200" blockAlign="center">
                  <Button size="slim" onClick={handleTestEvent} loading={testGoogle.isPending}>
                    {t.setup.sendTestEvent}
                  </Button>
                  {testResultMsg && <Text as="span" variant="bodySm" tone="subdued">{testResultMsg}</Text>}
                </InlineStack>
              )}
            </BlockStack>
          )}
        </InlineStack>

        {/* Consent honesty (audit P0-2): without a cookie banner + Consent Mode,
            EU and ad-blocked conversions cannot legally carry PII to Meta/GA4.
            Say it instead of letting the merchant discover missing conversions. */}
        <Text as="p" variant="bodySm" tone="subdued">{t.setup.consentNotice}</Text>

        {connectError && <Banner tone="critical">{connectError}</Banner>}

        <Divider />

        <BlockStack gap="300">
          {channels.map((ch) => (
            <ChannelRow
              key={ch.key}
              name={ch.name}
              status={status.channels[ch.key]}
              connecting={ch.pending}
              onConnect={() => handleConnect(ch.start)}
              onManage={() => navigate(ch.path)}
              t={t}
            />
          ))}
        </BlockStack>

        {status.funnelRouting && (
          <FunnelRouting
            routing={status.funnelRouting}
            declared={status.funnelDeclaredTags}
            channels={status.channels}
            pending={declareTags.isPending}
            onDeclare={handleDeclare}
            t={t}
          />
        )}
      </BlockStack>
      </Card>
    </BlockStack>
  );
}

// =============================================================================
// What each platform receives, and why
// =============================================================================
// This used to be a single per-shop switch, folded inside "Advanced options" -
// so a merchant could run the product for months without learning that the rest
// of the journey exists. It is now a per-channel state, in the open, with the
// reason spelled out.
//
// Nothing here decides anything. The server answers "may we forward this
// channel, and on what proof" (services/funnel/eligibility.ts) and hands back a
// reason CODE; this component maps the code to a translated sentence. A UI that
// re-derives the rule is a second owner of it, and would drift.
function FunnelRouting({
  routing, declared, channels, pending, onDeclare, t,
}: {
  routing: NonNullable<OnboardingStatus['funnelRouting']>;
  declared: OnboardingStatus['funnelDeclaredTags'];
  channels: OnboardingStatus['channels'];
  pending: boolean;
  onDeclare: (channel: FunnelChannelKey, keepsTheirTag: boolean) => void;
  t: ReturnType<typeof useTranslation>;
}) {
  const rows = (Object.keys(CHANNEL_LABELS) as FunnelChannelKey[])
    .filter((key) => channels[key]?.enabled)
    .map((key) => ({ key, decision: routing[key] }))
    .filter((r) => !!r.decision);

  if (rows.length === 0) return null;

  const sentence = (d: FunnelDecision): string => {
    const list = d.sources.join(', ');
    switch (d.reason) {
      case 'OWN_CONVERSION_ACTIONS': return t.setup.funnelWhyOwn;
      case 'BROWSER_TAG_STAMPED': return t.setup.funnelWhyStamped.replace('{sources}', list);
      case 'DESTINATION_NOT_FED': return t.setup.funnelWhyEmpty;
      case 'MERCHANT_SAYS_NO_OTHER_TAG': return t.setup.funnelWhyDeclared;
      // The three refusals share one sentence on purpose: from the merchant's
      // side they are the same fact - something else already feeds this
      // platform - and three phrasings of one fact reads as three problems.
      case 'COMPETING_TAG_NOT_STAMPED':
      case 'MERCHANT_KEEPS_THEIR_TAG':
      case 'DESTINATION_ALREADY_FED':
        return t.setup.funnelWhyCollision.replace('{sources}', list || '—');
      default: return '';
    }
  };

  // Ask when we have no answer yet, and keep the question available once they
  // have answered so they can change it: a merchant who removes their tag next
  // month needs a way back in, and our detection can be up to a month stale.
  const asks = (d: FunnelDecision) =>
    d.reason === 'UNVERIFIED'
    || d.reason === 'MERCHANT_KEEPS_THEIR_TAG'
    || d.proof === 'MERCHANT_DECLARED';

  return (
    <BlockStack gap="300">
      <Divider />
      <BlockStack gap="100">
        <Text as="h3" variant="headingSm">{t.setup.funnelChannelsTitle}</Text>
        <Text as="p" variant="bodySm" tone="subdued">{t.setup.funnelChannelsBody}</Text>
      </BlockStack>

      {rows.map(({ key, decision }) => (
        <Box key={key} paddingBlockStart="100">
          <BlockStack gap="150">
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodyMd" fontWeight="semibold">{CHANNEL_LABELS[key]}</Text>
              <Badge tone={decision.forward ? 'success' : undefined}>
                {decision.forward ? t.setup.funnelSends : t.setup.funnelPurchasesOnly}
              </Badge>
            </InlineStack>
            {sentence(decision) && (
              <Text as="p" variant="bodySm" tone="subdued">{sentence(decision)}</Text>
            )}
            {asks(decision) && (
              <BlockStack gap="100">
                <Text as="p" variant="bodySm">{t.setup.funnelAsk}</Text>
                <InlineStack gap="200">
                  <Button
                    size="slim"
                    pressed={declared?.[key] === true}
                    loading={pending}
                    onClick={() => onDeclare(key, true)}
                  >
                    {t.setup.funnelAskKeep}
                  </Button>
                  <Button
                    size="slim"
                    pressed={declared?.[key] === false}
                    loading={pending}
                    onClick={() => onDeclare(key, false)}
                  >
                    {t.setup.funnelAskOnly}
                  </Button>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">{t.setup.funnelAskWhy}</Text>
              </BlockStack>
            )}
          </BlockStack>
        </Box>
      ))}
    </BlockStack>
  );
}

const CHANNEL_LABELS: Record<FunnelChannelKey, string> = {
  googleAds: 'Google Ads',
  meta: 'Meta',
  ga4: 'Google Analytics 4',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
};
